<template>
  <component :is="currentIcon" v-if="currentIcon" />
</template>

<script lang="ts" setup>
import { defineAsyncComponent, computed, type Component } from 'vue';
import type { IconProps } from '@/types';

const iconMap: Record<IconProps['name'], () => Promise<{ default: Component }>> = {
  telegram: () => import('@/components/base/icon/social-media/telegram.vue'),
  instagram: () => import('@/components/base/icon/social-media/instagram.vue'),
  aparat: () => import('@/components/base/icon/social-media/aparat.vue'),
  discord: () => import('@/components/base/icon/social-media/discord.vue'),
  eta: () => import('@/components/base/icon/social-media/eta.vue'),
  facebook: () => import('@/components/base/icon/social-media/facebook.vue'),
  github: () => import('@/components/base/icon/social-media/github.vue'),
  linkedin: () => import('@/components/base/icon/social-media/linkedin.vue'),
  twitter: () => import('@/components/base/icon/social-media/twitter.vue'),
  whatsapp: () => import('@/components/base/icon/social-media/whatsapp.vue'),
  youtube: () => import('@/components/base/icon/social-media/youtube.vue'),
  error: () => import('@/components/base/icon/_icons/error.vue'),
  success: () => import('@/components/base/icon/_icons/success.vue'),
  unsuccess: () => import('@/components/base/icon/_icons/unsuccess.vue'),
  share: () => import('@/components/base/icon/_icons/share.vue'),
  download: () => import('@/components/base/icon/_icons/download.vue'),
  time: () => import('@/components/base/icon/_icons/time.vue'),
  calendar: () => import('@/components/icons/CalendarSchadule.vue'),
  invalidLink: () => import('@/components/icons/InvalidLinkSVG.vue'),
  location: () => import('@/components/base/icon/_icons/location.vue'),
  attach: () => import('@/components/base/icon/_icons/attach.vue'),
  close: () => import('@/components/base/icon/_icons/close.vue'),
  'arrow-left': () => import('@/components/base/icon/_icons/arrowLeft.vue'),
  'default-image': () => import('@/components/base/icon/_icons/defaultImage.vue'),
  copy: () => import('@/components/base/icon/_icons/copy.vue'),
  'arrow-blue': () => import('@/components/base/icon/_icons/arrowBlue.vue'),
  'event-location': () => import('@/components/base/icon/_icons/event-location.vue'),
  'event-time': () => import('@/components/base/icon/_icons/event-time.vue'),
  'go-to-link': () => import('@/components/base/icon/_icons/go-to-link.vue'),
  'invalid-receipt': () => import('@/components/base/icon/_icons/invalid-receipt.vue'),
  PRODUCT: () => import('@/components/base/icon/_icons/product.vue'),
  PAYMENT: () => import('@/components/base/icon/_icons/payment.vue'),
  RESERVATION: () => import('@/components/base/icon/_icons/reservation.vue'),
  SUBSCRIPTION: () => import('@/components/base/icon/_icons/subscription.vue'),
  phone: () => import('@/components/base/icon/contact/phone.vue'),
  email: () => import('@/components/base/icon/contact/email.vue'),
  address: () => import('@/components/base/icon/contact/location.vue'),
  receiptShare: () => import('@/components/base/icon/receipt-share/share.vue'),
  receiptDownload: () => import('@/components/base/icon/receipt-share/download.vue'),
  receiptCopy: () => import('@/components/base/icon/receipt-share/copy.vue'),
  toastSuccess: () => import('@/components/base/icon/toastIcons/success.vue'),
  toastError: () => import('@/components/base/icon/toastIcons/error.vue'),
  toastClose: () => import('@/components/base/icon/toastIcons/close.vue'),
  rating: () => import('@/components/base/icon/_icons/rating.vue'),
  'status-not-found': () => import('@/components/base/icon/status/not-found.vue'),
  'status-inactive': () => import('@/components/base/icon/status/inactive.vue'),
  'status-deadline': () => import('@/components/base/icon/status/reached-deadline.vue'),
  'status-capacity': () => import('@/components/base/icon/status/reached-capacity.vue'),
  'status-out-of-stock': () => import('@/components/base/icon/status/out-of-stock.vue'),
  'status-goal-reached': () => import('@/components/base/icon/status/reached-goal.vue'),
  'status-receipt-invalid': () => import('@/components/base/icon/status/invalid-receipt.vue'),
  'status-receipt-expired': () => import('@/components/base/icon/status/expired-receipt.vue'),
  'status-invalid': () => import('@/components/base/icon/status/invalid.vue'),
};

const props = defineProps<IconProps>();
const currentIcon = computed(() => {
  const componentLoader = iconMap[props.name as IconProps['name']];
  return componentLoader ? defineAsyncComponent(componentLoader) : null;
});
</script>
